import { StepContent, TextElement } from "types/types";

/*
  @function RichTextTranslator
  translate RichText to JSON
  @param text {array} - text to render
  @returns {React.Node}
*/
function RichTextTranslator({
  text,
  justText = false,
  marks = false,
}: {
  text?: any;
  justText?: boolean;
  marks?: boolean;
}): any {
  if (!text) return [];
  if (text[0] === "p" && text.length > 1) {
    return {
      type: "paragraph",
      content: RichTextTranslator({ text: text.slice(2) }),
    };
  }

  if (text[0] === "em" && text.length > 1) {
    if (marks) {
      return { type: "italic" };
    }

    if (justText) {
      return RichTextTranslator({ text: text.slice(2), justText: true });
    }

    return {
      type: "text",
      marks: [
        { type: "italic" },
        ...RichTextTranslator({ text: text.slice(2), marks: true }),
      ],
      text: RichTextTranslator({ text: text.slice(2), justText: true })[0],
    };
  }

  if (text[0] === "strong" && text.length > 1) {
    if (marks) {
      return { type: "bold" };
    }

    if (justText) {
      return RichTextTranslator({ text: text.slice(2), justText: true });
    }

    return {
      type: "text",
      marks: [
        { type: "bold" },
        ...RichTextTranslator({ text: text.slice(2), marks: true }),
      ],
      text: RichTextTranslator({ text: text.slice(2), justText: true })[0],
    };
  }

  if (text[0] === "term" && text.length > 1) {
    if (marks) {
      return {
        type: "term",
        attrs: {
          "data-term-id": text[1]["data-term-id"],
        },
      };
    }

    if (justText) {
      return RichTextTranslator({ text: text.slice(2), justText: true });
    }

    return {
      type: "text",
      marks: [
        {
          type: "term",
          attrs: {
            "data-term-id": text[1]["data-term-id"],
          },
        },
        ...RichTextTranslator({ text: text.slice(2), marks: true }),
      ],
      text: RichTextTranslator({ text: text.slice(2), justText: true })[0],
    };
  }

  if (text[0] === "web-link" && text.length > 1) {
    if (marks) {
      return {
        type: "web-link",
        attrs: {
          "data-web-link-id": text[1]["data-web-link"],
        },
      };
    }

    if (justText) {
      return RichTextTranslator({ text: text.slice(2), justText: true });
    }

    return {
      type: "text",
      marks: [
        {
          type: "web-link",
          attrs: {
            "data-web-link-id": text[1]["data-web-link-id"],
          },
        },
        ...RichTextTranslator({ text: text.slice(2), marks: true }),
      ],
      text: RichTextTranslator({ text: text.slice(2), justText: true })[0],
    };
  }

  if (text[0] === "ref" && text.length > 1) {
    if (marks) {
      return {
        type: "ref",
        attrs: {
          "data-ref-link": text[1]["data-ref-id"],
        },
      };
    }

    if (justText) {
      return RichTextTranslator({ text: text.slice(2), justText: true });
    }

    return {
      type: "text",
      marks: [
        {
          type: "ref",
          attrs: {
            "data-ref-id": text[1]["data-ref-id"],
          },
        },
        ...RichTextTranslator({ text: text.slice(2), marks: true }),
      ],
      text: " ",
    };
  }

  return text.flatMap((el: any, i: any) => {
    if (Array.isArray(el)) {
      return RichTextTranslator({ text: el, justText: justText, marks: marks });
    }
    if (justText) return el;

    if (marks) return [];

    if (el == "") {
      return [];
    }

    return { type: "text", text: el };
  });
}

/*
  @function JSONToArray
  translate  JSON RichText to
  @param text {object} - text to translate
  @returns {Array}
*/
export function JSONToArray(
  content: StepContent | StepContent[]
): TextElement[] | string {
  if (!Array.isArray(content)) {
    if (content.type === "paragraph") {
      return ["p", {}].concat(
        content.content ? JSONToArray(content.content) : ""
      ) as TextElement[];
    }

    if (content.marks) {
      for (let i = 0; i < content.marks.length; i++) {
        const m = content.marks[i];
        if (m.type === "bold") {
          const newText = {
            ...content,
            marks: content.marks.slice(1),
          };
          return ["strong", {}, JSONToArray(newText)] as TextElement[];
        }

        if (m.type === "italic") {
          const newText = {
            ...content,
            marks: content.marks.slice(1),
          };

          return ["em", {}, JSONToArray(newText)] as TextElement[];
        }

        if (m.type === "web-link") {
          const newText = {
            ...content,
            marks: content.marks.slice(1),
          };

          return [
            "web-link",
            { "data-web-link-id": m.attrs["data-web-link-id"] },
            JSONToArray(newText),
          ] as TextElement[];
        }

        if (m.type === "term") {
          const newText = {
            ...content,
            marks: content.marks.slice(1),
          };

          return [
            "term",
            { "data-term-id": m.attrs["data-term-id"] },
            JSONToArray(newText),
          ] as TextElement[];
        }

        if (m.type === "ref") {
          return ([
            "ref",
            { "data-ref-id": m.attrs["data-ref-id"] },
            "",
          ] as unknown) as TextElement[];
        }
      }
    }

    if (content.type === "text") {
      return content.text || "";
    }

    return "";
  } else {
    return content.map((el) => {
      if (typeof el === "object" && !Array.isArray(el) && el !== null) {
        return JSONToArray(el);
      }
    }) as TextElement[];
  }
}

export default RichTextTranslator;
